import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import avenueonelogo from "./avenueone.jpg";

const SERVICE_ID = "service_nk2uk5a";
const TEMPLATE_ID = "template_yn8oi1f";
const PUBLIC_KEY = "281zhmjZH2Ho6izB5";

// Template used for brochure download alerts (name + contact number).
// Create a second template in EmailJS and paste its ID here.
// Until then it falls back to the booking template so nothing breaks.
const BROCHURE_TEMPLATE_ID = "template_y78fl5u";

const SQUARE_BROCHURE =
    "https://drive.google.com/file/d/1b2SK_XHnEwesT8dY7NlRB19LnPq3qHYO/view?usp=sharing";
const PARK_BROCHURE =
    "https://drive.google.com/file/d/1C21qmBlD0jmu9csL0Mhh1w_IPq6QKbef/view?usp=sharing";

const ordinalFloors = (max) => [
    "Ground Floor",
    ...Array.from({ length: max }, (_, i) => {
        const n = i + 1;
        const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
        return `${n}${suffix} Floor`;
    }),
];

const SQUARE_FLOORS = ordinalFloors(13);
const PARK_FLOORS = ordinalFloors(5);

const SQUARE_TYPES = [
    { value: "One Bed Apartment", label: "1 Bed Apartment" },
    { value: "Two Bed Apartment", label: "2 Bed Apartment" },
];

const PARK_TYPES = [
    ...SQUARE_TYPES,
    { value: "Three Bed Apartment", label: "3 Bed Apartment" },
];

const emptyForm = {
    project: "",
    apartmentType: "",
    parkFloorChoice: "",
    squareFloorChoice: "",
    name: "",
    fatherName: "",
    email: "",
    contactNumber: "",
};

const inputClass = "w-full border border-gray-300 rounded-md p-2 bg-gray-100";
const labelClass = "block font-bold mb-2 text-lg max-sm:text-[16px]";

const BookingForm = () => {
    const [formData, setFormData] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox" && name === "project") {
            setFormData((prev) => ({
                ...prev,
                project: checked ? value : "",
                apartmentType: "",
                parkFloorChoice: "",
                squareFloorChoice: "",
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const {
            project,
            apartmentType,
            parkFloorChoice,
            squareFloorChoice,
            name,
            fatherName,
            email,
            contactNumber,
        } = formData;

        if (!project) {
            alert("Please select a Project.");
            return;
        }

        if (!apartmentType) {
            alert("Please select an Apartment Type.");
            return;
        }

        if (!name || !contactNumber) {
            alert("Please fill in your Name and Contact Number.");
            return;
        }

        setIsSubmitting(true);

        const preferredFloor =
            (project === "The Square" ? squareFloorChoice : parkFloorChoice) ||
            "Not specified";

        const emailData = {
            project,
            apartmentType,
            preferredFloor,
            name,
            fatherName,
            email,
            contactNumber,
        };

        try {
            await emailjs.send(SERVICE_ID, TEMPLATE_ID, emailData, PUBLIC_KEY);
            alert("Form submitted successfully!");
            setFormData(emptyForm);
        } catch (error) {
            console.error("Error sending email:", error);
            alert("Failed to submit the form. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBrochureDownload = (e, brochureName) => {
        if (!formData.name || !formData.contactNumber) {
            e.preventDefault();
            alert("Please enter your Name and Contact Number before downloading the brochure.");

            if (!formData.name) {
                document.getElementsByName("name")[0]?.focus();
            } else {
                document.getElementsByName("contactNumber")[0]?.focus();
            }
            return;
        }

        // Name + Contact Number are filled in: email them to the team,
        // then let the link open the brochure normally (target="_blank").
        emailjs
            .send(
                SERVICE_ID,
                BROCHURE_TEMPLATE_ID,
                {
                    brochure: brochureName,
                    name: formData.name,
                    contactNumber: formData.contactNumber,
                    email: formData.email || "Not provided",
                },
                PUBLIC_KEY
            )
            .catch((error) =>
                console.error("Error sending brochure download notification:", error)
            );
    };

    const isSquare = formData.project === "The Square";
    const isPark = formData.project === "The Park";
    const apartmentTypes = isSquare ? SQUARE_TYPES : PARK_TYPES;

    return (
        <div className="flex items-center justify-center">
            <div className="pt-12 pb-[100px] flex flex-col items-center justify-center gap-y-6 mt-[110px] max-w-[60%] w-full max-md:max-w-full">
                <div className="flex flex-col gap-y-4 items-center justify-center text-[#24272D] max-sm:px">
                    <h1 className="uppercase font-bold text-4xl max-sm:text-3xl">Application Form</h1>
                    <h1 className="font-bold text-xl">Residential Apartments</h1>
                    <img className="w-52" data-aos="zoom-in" src={avenueonelogo} alt="Logo" />
                </div>

                <div className="w-full px-32 max-sm:px-6 text-[#24272D] pt-8">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className={labelClass}>
                                Project <span className="text-red-500">*</span>
                            </label>
                            <div className="flex max-mobile:flex-col items-center max-mobile:items-start gap-x-4 max-mobile:gap-y-2 max-mobile:text-sm">
                                {["The Square", "The Park"].map((p) => (
                                    <label key={p} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            name="project"
                                            value={p}
                                            checked={formData.project === p}
                                            onChange={handleChange}
                                            className="mr-2"
                                        />
                                        {p}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {(isSquare || isPark) && (
                            <>
                                <div className="mb-4">
                                    <label className={labelClass}>
                                        Apartment Type <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center gap-x-8 max-sm:flex-col max-sm:items-start">
                                        {apartmentTypes.map((t) => (
                                            <label key={t.value} className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="apartmentType"
                                                    value={t.value}
                                                    checked={formData.apartmentType === t.value}
                                                    onChange={handleChange}
                                                    className="mr-2"
                                                />
                                                {t.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className={labelClass}>
                                        Floor Choice - {formData.project}
                                    </label>
                                    <select
                                        name={isSquare ? "squareFloorChoice" : "parkFloorChoice"}
                                        value={
                                            isSquare
                                                ? formData.squareFloorChoice
                                                : formData.parkFloorChoice
                                        }
                                        onChange={handleChange}
                                        className={inputClass}
                                    >
                                        <option value="">Select Floor</option>
                                        {(isSquare ? SQUARE_FLOORS : PARK_FLOORS).map((floor) => (
                                            <option key={floor} value={floor}>
                                                {floor}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}

                        <div className="mb-4">
                            <label className={labelClass}>
                                Applicant Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter your name"
                                className={inputClass}
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className={labelClass}>Father's Name</label>
                            <input
                                type="text"
                                name="fatherName"
                                value={formData.fatherName}
                                onChange={handleChange}
                                placeholder="Enter your father's name"
                                className={inputClass}
                            />
                        </div>

                        <div className="mb-4">
                            <label className={labelClass}>
                                Contact Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                placeholder="Enter your contact number"
                                className={inputClass}
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className={labelClass}>Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter your email"
                                className={inputClass}
                            />
                        </div>

                        <div className="mb-4">
                            <h1 className="font-bold pb-3 text-lg max-sm:text-[16px]">Download E-Brochure</h1>
                            <p className="text-sm text-gray-500 mb-3">
                                Please fill in your Name and Contact Number above to download a brochure.
                            </p>
                            <div className="flex gap-4 max-md:flex-col max-sm:text-sm">
                                <a
                                    href={SQUARE_BROCHURE}
                                    className="bg-[#066AAB] p-3 rounded-lg text-white"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleBrochureDownload(e, "The Square Brochure")}
                                >
                                    The Square Brochure
                                </a>

                                <a
                                    href={PARK_BROCHURE}
                                    className="bg-[#066AAB] p-3 rounded-lg text-white"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleBrochureDownload(e, "The Park Brochure")}
                                >
                                    The Park Brochure
                                </a>
                            </div>
                        </div>

                        <div className="mt-6">
                            <button
                                type="submit"
                                className={`w-full bg-[#24272D] font-semibold text-lg hover:border-4 hover:border-[#9E915C] hover:outline-offset-2 text-white py-3 rounded-md ${
                                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BookingForm;